import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_CUSTOMERS, locationData, END_POINT_DEPARTMENT, END_POINT_SUB_INDUSTRIES, END_POINT_INDUSTRIES } from "../const";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import EditFormLayout from "../components/EditFormLayout";
import EditFormActions from "../components/EditFormActions";
import SearchableSelect, {
  FormikSearchableSelect,
  buildSelectOptions,
  buildStringSelectOptions,
} from "../components/SearchableSelect";
import CompanyCheckboxPicker from '../components/CompanyCheckboxPicker';


export const CustomerEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate()

  const rawToken = sessionStorage.getItem('quotetoken');
  const token = rawToken?.replace(/^"|"$/g, '');
  const [loading, setLoading] = useState(true); // true initially to show loader
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [formErrors, setFormErrors] = useState([]);
  const [department, setDepartment] = useState([]);
  const [subIndustry, setSubIndustry] = useState([]);
  const [industry, setIndustry] = useState([]);
  const [originalContactForms, setOriginalContactForms] = useState([]);

  const [loadingSubIndustries, setLoadingSubIndustries] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [allowMultipleCompanies, setAllowMultipleCompanies] = useState(false);

  const [initialValues, setInitialValues] = useState({name: "", mobile: "", address_line1: "", address_line2: "", country: "", state: "", city: "", pincode: "", gst: "", industry_segment: "", industry_sub_segment: "", company_ids: [] });
  const [contactForms, setContactForms] = useState([{name: "", email: "", phone: "", designation: "", department: "", customer_id: ""}]);
  // const [contactForms, setContactForms] = useState([{name: "", email: "", phone: "", designation: "", department: "", customer_id: "7"}]);
  

  const addNewContact = () => {
    setContactForms([
      ...contactForms,
    { name: "", email: "", phone: "", designation: "", department: "", customer_id: `${id}`}]);
  };

  useEffect(() => {
    const fetchDepartmentlist = async () => {
      try {
        const response = await axios.get(API_URL + END_POINT_DEPARTMENT, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          }
        });

        if (response.data.status && Array.isArray(response.data.data)) {
          setDepartment(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching department:', error);
      }
    };

    const timeout = setTimeout(() => {
      fetchDepartmentlist();
    });

    return () => clearTimeout(timeout);
  }, []);

  const contactValidationSchema = Yup.object({
    name: Yup.string().required("Customer name is required"),
    email: Yup.string().email("Invalid email format").required("Email is required"),
    // phone: Yup.string()
    //   // .required("Mobile number is required")
    //   .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits"),
    phone: Yup.string()
      .nullable()
      .matches(/^\d{10}$/, {
        message: "Mobile number must be exactly 10 digits",
        excludeEmptyString: true,
    }),
    designation: Yup.string().required("Designation is required"),
    department: Yup.string()
      .notOneOf(["0"], "Please select a valid department")
      .required("Please select a department"),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true, // This allows formik to reset when initialValues change
    validationSchema: Yup.object({
      name: Yup.string().required("Customer name is required"),
      mobile: Yup.string()
      // .required("Mobile number is required")
      .nullable()
      .matches(/^\d{10}$/, "Number must be exactly 10 digits"),
      address_line1: Yup.string().required("Address is required"),
      country: Yup.string()
        .notOneOf(["0"], "Please select a valid country")
        .required("please select a country"),
      state: Yup.string()
        .notOneOf(["0"], "Please select a valid state")
        .required("please select a state"),
      city: Yup.string()
        .notOneOf(["0"], "Please select a valid city")
        .required("please select a city"),
      pincode: Yup.string().required("Pin code is required"),
      // gst: Yup.string().required("GST no. is required"),
      industry_segment: Yup.string()
        .notOneOf(["0"], "Please select a valid industry")
        .required("Industry Segment is required"),
      // industry_sub_segment: Yup.string().required("Industry Sub Segment is required")
      company_ids: allowMultipleCompanies ? Yup.array().min(1, "Select at least one company") : Yup.array(),
    }),
    onSubmit: async (values) => {

      const contactFormsValid = validateContactForms();
      if (!contactFormsValid) return;
            
      try {
        const payload = {
          ...values,
          representatives: contactForms
        };

        const response = await axios.put(`${API_URL + END_POINT_CUSTOMERS}/${id}`, payload, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          }
        });
        showSuccessToast(response.data.message)
          navigate('/customers');
      } catch (error) {
        showErrorToast(error.response?.data?.message || 'Failed to update customer.')
      }
    }
  });

  useEffect(() => {
    axios.get(`${API_URL}auth/context`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      setCompanies(data.data?.companies || []);
      setAllowMultipleCompanies(data.data?.allow_multiple_companies || false);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const response = await axios.get(`${API_URL + END_POINT_CUSTOMERS}/${id}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          }
        });

        if (response.data?.status) {
          const { name, mobile, address_line1, address_line2, country, state, city, pincode, gst, industry_segment, industry_sub_segment, representatives, companies: customerCompanies } = response.data.data;
          // setInitialValues({ name, mobile, address_line1, address_line2, country, state, city, pincode, gst, industry_segment, industry_sub_segment }); // Updates initial values to allow formik.dirty to work\
          setInitialValues({ name, mobile, address_line1, address_line2, country, state, city, pincode, gst, industry_segment: industry_segment?.id || "0",
            industry_sub_segment: industry_sub_segment?.id || "0", }); // Updates initial values to allow formik.dirty to work\
          setInitialValues((current) => ({
            ...current,
            name, mobile, address_line1, address_line2, country, state, city, pincode, gst,
            industry_segment: industry_segment?.id || "0",
            industry_sub_segment: industry_sub_segment?.id || "0",
            company_ids: (customerCompanies || []).map((company) => String(company.id)),
          }));
          if (Array.isArray(representatives)) {
            setContactForms(representatives);
            setOriginalContactForms(representatives);
          }
          //Fetch sub-industry list based on current industry
          if (industry_segment?.id) {
            fetchSubIndustries(industry_segment.id);
          }
        }
      } catch (error) {
        console.error('Error fetching customer:', error);
      } finally {
        setLoading(false); // hide loader in all cases
      }
    };

    fetchCustomerDetails();

  }, [id]);


  // const isContactFormDirty = JSON.stringify(contactForms) !== JSON.stringify(originalContactForms);
  const isContactFormDirty = useMemo(() => {
  return JSON.stringify(contactForms) !== JSON.stringify(originalContactForms);
}, [contactForms, originalContactForms]);


  const validateContactForms = () => {
    let isValid = true;
    const newErrors = [];

    contactForms.forEach((form, index) => {
      const errors = {};

      // Check if any field is partially filled (i.e., user intends to add contact)
      const isAnyFieldFilled = Object.values(form).some(val => val && val !== "0");

      if (isAnyFieldFilled) {
        try {
          contactValidationSchema.validateSync(form, { abortEarly: false });
        } catch (validationErrors) {
          isValid = false;
          validationErrors.inner.forEach(err => {
            errors[err.path] = err.message;
          });
        }
      }

      newErrors.push(errors);
    });

    setFormErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    if (!loading) {
      const country = initialValues.country;
      const state = initialValues.state;

      if (country && locationData[country]) {
        setStateList(Object.keys(locationData[country].states));
      }

      if (country && state && locationData[country]?.states[state]) {
        setCityList(locationData[country].states[state]);
      }
    }
  }, [loading]);

  useEffect(() => {
    const country = formik.values.country;
    if (country && locationData[country]) {
      setStateList(Object.keys(locationData[country].states));

      // Reset only if the selected state is not part of the country
      if (!locationData[country].states[formik.values.state]) {
        formik.setFieldValue("state", "");
        formik.setFieldValue("city", "");
        setCityList([]);
      }
    }
  }, [formik.values.country]);

  useEffect(() => {
    const country = formik.values.country;
    const state = formik.values.state;
    if (country && state && locationData[country]?.states[state]) {
      setCityList(locationData[country].states[state]);

      if (!locationData[country].states[state].includes(formik.values.city)) {
        formik.setFieldValue("city", "");
      }
    }
  }, [formik.values.state]);

  // const handleContactChange = (index, field, value) => {
  //   const updatedForms = [...contactForms];
  //   updatedForms[index][field] = value;
  //   setContactForms(updatedForms);
  // };
  const handleContactChange = (index, field, value) => {
  const updatedForms = contactForms.map((form, i) =>
    i === index ? { ...form, [field]: value } : form
  );
  setContactForms(updatedForms);
};


  const removeContact = (indexToRemove) => {
    setContactForms((prevForms) =>
      prevForms.filter((_, index) => index !== indexToRemove)
    );
  };

  useEffect(() => {
    const fetchIndustrylist = async () => {
      try {
        const response = await axios.get(API_URL + END_POINT_INDUSTRIES, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          }
        });

        if (response.data.status && Array.isArray(response.data.data)) {
          setIndustry(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching industry:', error);
      } 
    };

    const timeout = setTimeout(() => {
      fetchIndustrylist();
    });

    return () => clearTimeout(timeout);
  }, [token]);


  const fetchSubIndustries = async (industryId) => {
    if (!industryId || industryId === "0") {
      setSubIndustry([]);
      return;
    }

    setLoadingSubIndustries(true);
        
    try {
      const response = await axios.get(`${API_URL}${END_POINT_SUB_INDUSTRIES}/${industryId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status && Array.isArray(response.data.data)) {
        setSubIndustry(response.data.data);
      } else {
        setSubIndustry([]);
      }
    } catch (error) {
      console.error("Error fetching sub-industries:", error);
      setSubIndustry([]);
    } finally {
      setLoadingSubIndustries(false); // Stop loading
    }
  };


  const handleIndustryChange = async (selectedIndustryId) => {
    formik.setFieldValue("industry_segment", selectedIndustryId);
    formik.setFieldValue("industry_sub_segment", "0"); // Reset sub segment
    fetchSubIndustries(selectedIndustryId); // Fetch new sub-industry list
  };

  const industryOptions = useMemo(() => buildSelectOptions(industry), [industry]);
  const subIndustryOptions = useMemo(() => buildSelectOptions(subIndustry), [subIndustry]);
  const countryOptions = useMemo(
    () => buildStringSelectOptions(Object.keys(locationData)),
    []
  );
  const stateOptions = useMemo(() => buildStringSelectOptions(stateList), [stateList]);
  const cityOptions = useMemo(() => buildStringSelectOptions(cityList), [cityList]);
  const departmentOptions = useMemo(() => buildSelectOptions(department), [department]);

  return (
    <EditFormLayout
      title="Edit Customer"
      subtitle="Update customer profile and contacts"
      backTo="/customers"
      loading={loading}
    >
      <Form noValidate onSubmit={formik.handleSubmit}>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="name">
                    <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("name")} isInvalid={formik.touched.name && formik.errors.name}/>
                    <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                      
                <Col md={6} className="mb-3">
                  <Form.Group controlId="mobile">
                    <Form.Label>Mobile no.</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("mobile")} isInvalid={formik.touched.mobile && formik.errors.mobile}/>
                    <Form.Control.Feedback type="invalid"> {formik.errors.mobile} </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {(allowMultipleCompanies || companies.length > 1) && (
                <Row>
                  <Col md={12} className="mb-3">
                    <CompanyCheckboxPicker
                      companies={companies}
                      selectedIds={formik.values.company_ids}
                      onChange={(companyIds) => formik.setFieldValue('company_ids', companyIds)}
                      error={formik.touched.company_ids && formik.errors.company_ids}
                    />
                  </Col>
                </Row>
              )}
  
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="industry_segment">
                    <Form.Label>Industry Segment <span className="text-danger">*</span></Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="industry_segment"
                      options={industryOptions}
                      placeholder="Search industry..."
                      onChange={handleIndustryChange}
                    />
                  </Form.Group>
                </Col>
  
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="industry_sub_segment">
                    <Form.Label>Industry Sub Segment</Form.Label>
                    {loadingSubIndustries ? (
                      <div className="d-flex align-items-center">
                        <div className="spinner-border text-primary me-2" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Loading Sub Segments...</span>
                      </div>
                    ) : (
                      <FormikSearchableSelect
                        formik={formik}
                        name="industry_sub_segment"
                        options={subIndustryOptions}
                        placeholder="Search sub segment..."
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
  
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="address_line1">
                    <Form.Label>Address line 1</Form.Label>
                    <Form.Control as="textarea" {...formik.getFieldProps("address_line1")} isInvalid={formik.touched.address_line1 && formik.errors.address_line1}/>
                    <Form.Control.Feedback type="invalid">{formik.errors.address_line1}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="address_line2">
                    <Form.Label>Address line 2</Form.Label>
                    <Form.Control as="textarea" {...formik.getFieldProps("address_line2")} isInvalid={formik.touched.address_line2 && formik.errors.address_line2}/>
                    <Form.Control.Feedback type="invalid">{formik.errors.address_line2}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
  
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="pincode">
                    <Form.Label>Pin Code</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("pincode")} isInvalid={formik.touched.pincode && formik.errors.pincode}/>
                    <Form.Control.Feedback type="invalid">{formik.errors.pincode}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                        
                <Col md={6} className="mb-3">
                  <Form.Group controlId="gst">
                    <Form.Label>GST NO.</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("gst")} isInvalid={formik.touched.gst && formik.errors.gst} />
                    <Form.Control.Feedback type="invalid">{formik.errors.gst}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
  
              <Row>
                <Col sm={4} className="mb-3">
                  <Form.Group controlId="country">
                    <Form.Label>Country</Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="country"
                      options={countryOptions}
                      placeholder="Search country..."
                    />
                  </Form.Group>
                </Col>
                          
                <Col sm={4} className="mb-3">
                  <Form.Group controlId="state">
                    <Form.Label>State</Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="state"
                      options={stateOptions}
                      placeholder="Search state..."
                      isDisabled={!formik.values.country}
                    />
                  </Form.Group>
                </Col>
                          
                <Col sm={4} className="mb-3">
                  <Form.Group controlId="city">
                    <Form.Label>City</Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="city"
                      options={cityOptions}
                      placeholder="Search city..."
                      isDisabled={!formik.values.state}
                    />
                  </Form.Group>
                </Col>
              </Row>
  
              {contactForms.length === 0 ? (
                <div className="d-flex justify-content-start mt-3">
                  <Button variant="secondary" onClick={addNewContact}>Add new customer contact</Button>
                </div>
                ) : (
                  contactForms.map((form, index) => (
                    <Card key={index} border="light" className="bg-white shadow-sm mb-4">
  
                    <Button
                        variant="light"
                        className="position-absolute top-0 end-0 m-2 p-1"
                        onClick={() => removeContact(index)}
                        style={{ border: 'none', fontSize: '1.2rem', lineHeight: '1' }}
                        aria-label="Remove contact"
                    >
                        ×
                    </Button>
  
                    <Card.Body>
                      <h5 className="mb-4">Customer Contact {index + 1}</h5>
                      <Form>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                              {/* <Form.Control type="text" value={form.name} onChange={(e) => handleContactChange(index, "name", e.target.value)}/> */}
                              <Form.Control
                                type="text"
                                placeholder="Enter name"
                                value={contactForms[index].name}
                                isInvalid={!!formErrors[index]?.name}
                                onChange={(e) => handleContactChange(index, "name", e.target.value)}
                                onBlur={(e) => validateContactForms(index, "name", e.target.value)}
                              />
                              <Form.Control.Feedback type="invalid">
                              {formErrors[index]?.name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
            
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                type="text"
                                placeholder="Enter email"
                                value={contactForms[index].email}
                                isInvalid={!!formErrors[index]?.email}
                                onChange={(e) => handleContactChange(index, "email", e.target.value)}
                                onBlur={(e) => validateContactForms(index, "email", e.target.value)}
                              />
                                <Form.Control.Feedback type="invalid">
                                {formErrors[index]?.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
            
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Mobile no.</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter phone"
                                value={contactForms[index].phone}
                                isInvalid={!!formErrors[index]?.phone}
                                onChange={(e) => handleContactChange(index, "phone", e.target.value)}
                                onBlur={(e) => validateContactForms(index, "phone", e.target.value)}
                              />
                              <Form.Control.Feedback type="invalid">
                              {formErrors[index]?.phone}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col sm={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Department <span className="text-danger">*</span></Form.Label>
                              <SearchableSelect
                                options={departmentOptions}
                                value={form.department}
                                onChange={(value) => {
                                  handleContactChange(index, "department", value);
                                  validateContactForms(index, "department", value);
                                }}
                                onBlur={() => validateContactForms(index, "department", form.department)}
                                placeholder="Search department..."
                                isInvalid={!!formErrors[index]?.department}
                                errorMessage={formErrors[index]?.department}
                              />
                            </Form.Group>
                          </Col>                                       
                        </Row>
                              
                        <Row>
                          <Col sm={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Designation <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter designation"
                                value={contactForms[index].designation}
                                isInvalid={!!formErrors[index]?.designation}
                                onChange={(e) => handleContactChange(index, "designation", e.target.value)}
                                onBlur={(e) => validateContactForms(index, "designation", e.target.value)}
                              />
                              <Form.Control.Feedback type="invalid">
                              {formErrors[index]?.designation}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
    
                        {index === contactForms.length - 1 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button variant="secondary" onClick={addNewContact}>Add new customer contact</Button>
                          </div>
                        )}
                      </Form>
                    </Card.Body>
                  </Card>
                ))
              )}
              <EditFormActions
                backTo="/customers"
                isDirty={formik.dirty || isContactFormDirty}
              />
            </Form>
    </EditFormLayout>
  );
};